import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Grid,
  Stack,
  Avatar,
  Chip,
  Divider,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import { formatCurrency as formatCurrencyFromConstants } from '../../constants/currency';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  Notes as NotesIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { Customer, Site } from '../../feature/orders/order';
import { formatStatusText } from '../../utils/formatters';
import CascadingSidePanel from './CascadingSidePanel';

interface CustomerSiteDetailsSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseAll?: () => void;
  customer: Customer | null;
  site: any | null;
  order?: any;
  title?: string;
  onBackToOrders?: () => void;
}

const CustomerSiteDetailsSidePanel: React.FC<CustomerSiteDetailsSidePanelProps> = ({
  isOpen,
  onClose,
  onCloseAll,
  customer,
  site,
  order,
  title = "Customer & Site Details",
  onBackToOrders
}) => {
  const theme = useTheme();

  if (!customer) return null;

  useEffect(() => {
    console.log("customer-->",customer);
    console.log("site-->",site);
  },[customer,site])

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'info';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: string | number) => {
    return formatCurrencyFromConstants(amount, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <CascadingSidePanel
      isOpen={isOpen}
      onClose={onClose}
      onCloseAll={onCloseAll}
      panelId="customer-site-details-panel"
      title={title}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
          {/* Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {onBackToOrders && (
                <IconButton 
                  onClick={onBackToOrders}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  ←
                </IconButton>
              )}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
            </Box>
            <IconButton 
              onClick={onCloseAll || onClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {/* Customer Information */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {/* <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 48, height: 48 }}>
                    <PersonIcon />
                  </Avatar> */}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {customer.name}
                    </Typography>
                    {/* <Typography variant="body2" color="text.secondary">
                      Customer ID: {customer.customer_tracking_code || customer.id}
                    </Typography> */}
                  </Box>
                  {customer.customerType && (
                    <Chip
                      label={customer.customerType.toUpperCase()}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{ ml: 'auto', fontWeight: 600 }}
                    />
                  )}
                </Box>

                <Grid container spacing={3}>
                  {/* Contact Information */}
                  <Grid item xs={12} md={6}>
                    {/* <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                      Contact Information
                    </Typography> */}
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PhoneIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Phone
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                            {customer.phone || 'Not provided'}
                          </Typography>
                        </Box>
                      </Box>
                      {
                        customer.email  && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <EmailIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                Email
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                {customer.email || 'Not provided'}
                              </Typography>
                            </Box>                        
                        
                      </Box>
                       )
                        }
                        {
                        customer.contactPerson  && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Contact Person
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                            {customer.contactPerson || 'Not specified'}
                          </Typography>
                        </Box>
                      </Box>
                        )}
                    </Stack>
                  </Grid>

                  {/* Address Information */}
                  <Grid item xs={12} md={6}>
                    {/* <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                      Address Information
                    </Typography> */}
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <LocationIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Address
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                            {customer?.address }
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {customer.city && customer.state ? `${customer.city}, ${customer.state}` : ''}
                            {customer.zipCode && ` - ${customer.zipCode}`}
                          </Typography>
                        </Box>
                      </Box>
                      {customer.industry && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <WorkIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Industry
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                              {customer.industry}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Grid>

                  {/* Additional Information */}
                  {(customer.creditLimit || customer.status !== undefined) && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                        Additional Information
                      </Typography>
                      <Grid container spacing={2}>
                        {customer.creditLimit && (
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <MoneyIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  Credit Limit
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {formatCurrency(customer.creditLimit)}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Status
                              </Typography>
                              <Chip
                                label={customer.status ? 'Active' : 'Inactive'}
                                color={customer.status ? 'success' : 'default'}
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Site Information */}
            {site && (
              <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {/* <Avatar sx={{ bgcolor: 'secondary.main', mr: 2, width: 48, height: 48 }}>
                      <HomeIcon />
                    </Avatar> */}
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }} >
                        {site.name}
                      </Typography>
                      {/* <Typography variant="body2" color="text.secondary">
                        Site ID: {site.site_tracking_code || site.id}
                      </Typography> */}
                    </Box>
                    {site.status && (
                      <Chip
                        label={site.status.toUpperCase()}
                        color={getStatusColor(site.status) as any}
                        size="small"
                        sx={{ ml: 'auto', fontWeight: 600 }}
                      />
                    )}
                  </Box>

                  <Grid container spacing={3}>
                    {/* Site Contact Information */}
                    <Grid item xs={12} md={6}>
                      {/* <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'secondary.main' }}>
                        Site Contact
                      </Typography> */}
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Contact Person
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                              {site.contactPerson || 'Not specified'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <PhoneIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Contact Phone
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                              {site.contactPhone || 'Not provided'}
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>
                    </Grid>

                    {/* Site Address */}
                    <Grid item xs={12} md={6}>
                      {/* <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'secondary.main' }}>
                        Site Address
                      </Typography> */}
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <LocationIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5 }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }} >
                              Address
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                              {site.address || 'Not provided'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {site.city && site.state ? `${site.city}, ${site.state}` : ''}
                              {site.zipCode && ` - ${site.zipCode}`}
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>
                    </Grid>

                    {/* Site Statistics */}
                    {(site.totalOrder !== undefined || site.completedOrder !== undefined || site.pendingOrder !== undefined) && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'secondary.main' }}>
                          Site Statistics
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {site.totalOrder || 0}
                              </Typography>
                              <Typography variant="body2">
                                Total Orders
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={4}>
                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {site.completedOrder || 0}
                              </Typography>
                              <Typography variant="body2">
                                Completed
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={4}>
                            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {site.pendingOrder || 0}
                              </Typography>
                              <Typography variant="body2">
                                Pending
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}

                    {/* Site Notes */}
                    {site.notes && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <NotesIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.5 }} />
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Notes
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {site.notes}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Order Summary */}
            {order && (
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ bgcolor: 'info.main', mr: 2, width: 48, height: 48 }}>
                      <WorkIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Current Order Summary
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Order #{order.orderNumberNew || order.orderNumber || order.order_tracking_code || order.id}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.75rem'}} >
                          {formatCurrency(order.totalAmount)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Total Amount
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main', fontSize: '0.75rem'}}>
                          {order.products?.length || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Sets
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', }}>
                        <Chip
                          label={formatStatusText(order.status || '') || 'Unknown'}
                          color={getStatusColor(order.status) as any}
                          sx={{ fontWeight: 300,  fontSize: '0.75rem',}}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Status
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip
                          label={formatStatusText(order.priority || '')|| 'NORMAL'}
                          color={getPriorityColor(order.priority) as any}
                          sx={{ fontWeight: 300, fontSize: '0.75rem'}}
                          
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          Priority
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {order.dueDate && (
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 , ml:-2}}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ScheduleIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary">
                          Due Date: <strong>{formatDate(order.dueDate)}</strong>
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
    </CascadingSidePanel>
  );
};

export default CustomerSiteDetailsSidePanel;
